import type { RepCode } from '@interfaces/repCode';
import Search from '@mui/icons-material/Search';
import { AccountTypes } from '@onevesthq/ov-enums';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import {
  InputAdornment,
  TextField,
} from '@ovComponents/2-component/textField/textField';
import { BetaBadge } from '@ovComponents/3-pattern/betaBadge/betaBadge';
import { RepCodeSelect } from '@ovComponents/3-pattern/repCodeSelect/repCodeSelect';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { FlagsmithFlags } from '@utils/flagsmith';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type {
  ContinueFunctionArgs,
  CreateAccountOptionsType,
} from './createAccount';

type AccountListItem = {
  weight: number;
  type: string;
};

// custom weighted list by Josh
const generateWeightedList = (accountTypeList: string[]): AccountListItem[] => {
  const weightedList = accountTypeList.map((accountItem: string) => {
    if (accountItem === AccountTypes.PERSONAL) {
      return {
        weight: 1,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.CASH_JOINT) {
      return {
        weight: 2,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.TFSA) {
      return {
        weight: 3,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RRSP) {
      return {
        weight: 4,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RRSP_SPOUSAL) {
      return {
        weight: 5,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_FAMILY) {
      return {
        weight: 6,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_SINGLE) {
      return {
        weight: 7,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_ADULT) {
      return {
        weight: 8,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_FAMILY_JOINT) {
      return {
        weight: 9,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RESP_SINGLE_JOINT) {
      return {
        weight: 10,
        type: accountItem,
      };
    }
    if (accountItem === AccountTypes.RRIF) {
      return {
        weight: 11,
        type: accountItem,
      };
    }
    return {
      weight: 15,
      type: accountItem,
    };
  });
  return weightedList.sort((a, b) => a.weight - b.weight);
};

export const CreateAccountVisual = ({
  options,
  continueFunc,
  accountType,
  availableAccountTypes,
  getReusableRespTransferValue,
  workflowCompletion,
  loading,
  saving,
  selectedRepCode,
  setSelectedRepCode,
  repCodeError,
  setRepCodeError,
}: {
  options: CreateAccountOptionsType;
  loading: boolean;
  accountType: string;
  continueFunc: (continueFunctionArgs: ContinueFunctionArgs) => Promise<void>;
  availableAccountTypes: AccountTypes[];
  getReusableRespTransferValue?: (accountType: string) => boolean;
  workflowCompletion?: any;
  saving?: boolean;
  selectedRepCode?: RepCode;
  setSelectedRepCode?: (repCode: RepCode | undefined) => void;
  repCodeError?: boolean;
  setRepCodeError?: (error: boolean) => void;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation([
    'client',
    'accountTypes',
    'accountsDetail',
    'workflowCompletions',
  ]);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [accountList, setAccountList] = useState<string[]>([]);
  const [respDialogType, setRespDialogType] = useState<string | null>(null);
  const [
    isTransferredFromAnotherInstitution,
    setIsTransferredFromAnotherInstitution,
  ] = useState(false);
  const isRespInstitutionTransferEnabled = useFeatureFlag(
    FlagsmithFlags.resp_transferred_from_another_institution,
  );

  useEffect(() => {
    const bySearchTerm = (type: string) => {
      const techHaystack = type.toUpperCase();
      const translatedHaystack = t(`accountTypes:${type}`).toUpperCase();
      const needle = searchQuery.trim().toUpperCase();
      return (
        techHaystack.includes(needle) || translatedHaystack.includes(needle)
      );
    };

    setAccountList(availableAccountTypes.filter(bySearchTerm));
  }, [searchQuery, t, availableAccountTypes]);

  const onTileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (loading) return;
    const selectedAccountType = e.target.value;
    await setWorkflowData({
      ...workflowData,
      currentAccountType: selectedAccountType,
    });
    sendAnalytic(ovAnalyticsEvents.workflowsCreateAccountContinueButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });
    if (
      isRespInstitutionTransferEnabled &&
      selectedAccountType.includes('RESP')
    ) {
      // Pre-fill with the value already persisted on the account being reused so the user
      // sees the current DB state rather than a hard-coded default.
      setIsTransferredFromAnotherInstitution(
        getReusableRespTransferValue?.(selectedAccountType) ?? false,
      );
      setRespDialogType(selectedAccountType);
      return;
    }
    await continueFunc({ accountType: selectedAccountType });
  };

  const confirmRespAccountCreation = async () => {
    const selectedAccountType = respDialogType;
    setRespDialogType(null);
    if (!selectedAccountType) return;
    await continueFunc({
      accountType: selectedAccountType,
      isTransferredFromAnotherInstitution,
    });
  };

  if (!options) return <></>;

  const asValueLabelSubtitle = (a: AccountListItem) => ({
    value: a.type,
    label: t(`accountTypes:${a.type}`),
    subtitle: options.displayAccountDefinitions
      ? t(`accountsDetail:accountDescriptions.${a.type}`)
      : undefined,
  });

  return (
    <Form>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackendString(options?.title)}
        </Typography>
        <BetaBadge />
      </Box>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackendString(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      {setSelectedRepCode && (
        <RepCodeSelect
          repCode={selectedRepCode}
          setRepCode={(repCode) => {
            setSelectedRepCode(repCode);
            if (repCode) setRepCodeError?.(false);
          }}
          label={t('workflowCompletions:createAccount.repCodeLabel')}
          testId='create-account-rep-code'
          error={repCodeError}
          errorText={t('workflowCompletions:createAccount.repCodeRequired')}
          fullWidth
          sx={{ mb: 3 }}
        />
      )}
      <TextField
        testId='create-account-search-field'
        value={searchQuery}
        label=''
        fullWidth
        size='small'
        placeholder={t('accountsDetail:searchAccounts')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchQuery(e.target.value)
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <>
        <SelectionTile
          disabled={saving}
          testId='create-account-type'
          onChange={onTileSelect}
          value={accountType ?? ''}
          options={generateWeightedList(accountList).map(asValueLabelSubtitle)}
          type='completion'
        />

        {accountList.length === 0 && (
          <Typography
            variant='bodyLarge'
            sx={{ color: theme.color.onSurfaceVariant }}
          >
            {t('accountsDetail:noAccountOptions')}
          </Typography>
        )}
      </>
      <Dialog
        open={!!respDialogType}
        onClose={() => setRespDialogType(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle onClose={() => setRespDialogType(null)}>
          {t('workflowCompletions:respAccountCreation.transferDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t(
              'workflowCompletions:respAccountCreation.transferDialogAccountTypeLabel',
            )}
            value={respDialogType ? t(`accountTypes:${respDialogType}`) : ''}
            fullWidth
            size='small'
            InputProps={{ readOnly: true }}
            sx={{ mb: 3 }}
          />
          <SwitchField
            data-testid='resp-transferred-from-another-institution'
            label={t(
              'workflowCompletions:respAccountCreation.transferredFromAnotherInstitution',
            )}
            checked={isTransferredFromAnotherInstitution}
            onChange={setIsTransferredFromAnotherInstitution}
          />
        </DialogContent>
        <DialogFooter>
          <Button
            data-testid='resp-create-new-account'
            fullWidth
            loading={saving}
            disabled={saving}
            onClick={confirmRespAccountCreation}
          >
            {t('workflowCompletions:respAccountCreation.createNewAccount')}
          </Button>
        </DialogFooter>
      </Dialog>
    </Form>
  );
};

export default CreateAccountVisual;
